/** Alphabet, numbers and the sounds — the day-one page.
 *
 * The client asked for "basics like alphabets / numbers with sounds /
 * phonetics". Everything here is tappable and speaks in our own German
 * voice: a beginner cannot learn a sound from a written respelling, and
 * "Weh" on a page is only useful once you have heard it.
 *
 * DESIGNED FOR EIGHT-YEAR-OLDS AND TWENTY-SIX-YEAR-OLDS AT ONCE, which is
 * the actual constraint and worth writing down, because the obvious moves
 * for one are fatal for the other. A child needs it big, coloured and
 * visibly reactive; an adult abandons anything that looks like a toy. The
 * two agree on size, colour and responsiveness and disagree on mascots,
 * cartoons and cheerful baby copy — so this leans hard on the first three
 * and has none of the last three.
 *
 * Concretely: the character is the tile, at a size a thumb can hit and an
 * eye can read across a room; every tile carries one of four colour
 * washes from the theme so the grid reads as a set of objects rather than
 * a spreadsheet; and tapping does something visible, because an
 * eight-year-old will not read an instruction telling them it makes a
 * sound.
 *
 * Playback goes through speakBest, so it uses the server voice where there
 * is one and the browser's where there is not — a learner on a deployment
 * with no voice models still hears something.
 */
import { useState } from "react";
import { speakBest } from "../../utils/voice";
import { useT } from "../../i18n";
import type { MessageKey } from "../../i18n/en";
import {
  ALPHABET, EXTRA_LETTERS, NUMBERS_0_20, NUMBER_ORDER, SOUNDS, TENS,
} from "./basics";
import type { BasicRow } from "./basics";
import { Card } from "../../components/ui";

/** Four washes from the theme, rotated by position.
 *
 *  By INDEX rather than at random: the same letter keeps the same colour
 *  every visit, so a child who has learned where Z lives can still find
 *  it. Every pairing is a token pair the theme already proves readable —
 *  the point is a grid that looks like a set of objects, not a decision
 *  to invent colours here. */
const WASHES = [
  "bg-brand-soft text-brand-dark border-brand/30",
  "bg-accent-soft text-accent-dark border-accent/40",
  "bg-success/10 text-success border-success/30",
  "bg-brand/10 text-brand-dark border-brand/25",
];

const washFor = (index: number) => WASHES[index % WASHES.length];

/** A tappable tile. Square and large for a letter or a number, wide for a
 *  sound that carries an explanation. */
function SayTile(
  { row, index, wide = false }: { row: BasicRow; index: number; wide?: boolean },
) {
  const t = useT();
  const [saying, setSaying] = useState(false);

  const play = () => {
    setSaying(true);
    speakBest(row.say, { lang: "de-DE", onEnd: () => setSaying(false) });
  };

  return (
    <button
      type="button"
      onClick={play}
      aria-label={t(
        // A sound tile plays WORDS CONTAINING the pattern, not the pattern
        // itself - you cannot pronounce "sp / st" on its own. Saying which
        // is which is the difference between a learner understanding what
        // they just heard and thinking the app read the wrong line.
        row.sayIsExample ? "basics.playExamples" : "basics.play",
        { what: row.show },
      )}
      className={`relative rounded-card border-2 transition-shadow ${washFor(index)} ${
        wide ? "w-full p-4 text-left" : "grid aspect-square place-items-center p-2 text-center"
      } ${saying ? "tile-speaking shadow-lg ring-4 ring-brand/40" : "hover:shadow-md"}`}
    >
      {/* The one affordance a child does not have to read. It sits quietly
          until the tile is playing, then marks which one is making the
          sound — on a grid of thirty, that matters. */}
      <span
        aria-hidden
        className={`absolute right-1.5 top-1.5 text-xs transition-opacity ${
          saying ? "opacity-100" : "opacity-30"
        }`}
      >
        🔊
      </span>

      <span className="block">
        <span
          className={`block font-extrabold leading-none ${
            wide ? "text-xl" : "text-3xl sm:text-4xl"
          }`}
        >
          {row.show}
        </span>

        {/* Only when it really is a pronunciation of what is shown. For a
            sound pattern this line used to print the example words, right
            under the big character, which read as "ei is pronounced mein
            Wein" - and the examples are printed again below. */}
        {!row.sayIsExample && (
          <span className="mt-1 block text-xs font-semibold opacity-80">{row.say}</span>
        )}

        {row.note && (
          <span className="mt-2 block text-xs font-medium text-ink/80">
            {t(row.note as MessageKey)}
          </span>
        )}
        {row.example && (
          <span className="mt-1 block text-xs italic text-ink/70">
            {row.sayIsExample ? t("basics.hearThese", { words: row.example }) : row.example}
          </span>
        )}
      </span>
    </button>
  );
}

function Grid({ rows }: { rows: BasicRow[] }) {
  return (
    <div className="grid grid-cols-4 gap-2 sm:grid-cols-6 sm:gap-3">
      {rows.map((row, i) => (
        <SayTile key={row.show} row={row} index={i} />
      ))}
    </div>
  );
}

export function BasicsView() {
  const t = useT();
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted">{t("basics.intro")}</p>

      <Card title={t("basics.alphabet")}>
        <p className="text-sm text-muted">{t("basics.alphabetIntro")}</p>
        <div className="mt-3">
          <Grid rows={ALPHABET} />
        </div>
        <p className="mt-5 text-sm font-bold text-ink">{t("basics.extra")}</p>
        <p className="text-sm text-muted">{t("basics.extraIntro")}</p>
        <div className="mt-2">
          <Grid rows={EXTRA_LETTERS} />
        </div>
      </Card>

      <Card title={t("basics.numbers")}>
        <div className="mt-1">
          <Grid rows={NUMBERS_0_20} />
        </div>
        <p className="mt-5 text-sm font-bold text-ink">{t("basics.tens")}</p>
        <div className="mt-2">
          <Grid rows={TENS} />
        </div>
        <p className="mt-5 text-sm font-bold text-ink">{t("basics.backwards")}</p>
        <p className="text-sm text-muted">{t("basics.backwardsIntro")}</p>
        <div className="mt-2">
          <Grid rows={NUMBER_ORDER} />
        </div>
      </Card>

      <Card title={t("basics.sounds")}>
        <p className="text-sm text-muted">{t("basics.soundsIntro")}</p>
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
          {SOUNDS.map((row, i) => (
            <SayTile key={row.show} row={row} index={i} wide />
          ))}
        </div>
      </Card>
    </div>
  );
}
